import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  verifyKkiapayTransaction,
  registerKkiapayTransaction,
  KkiapayVerificationReport,
} from "../../../services/kkiapayAdminAPI";
import { extractAxoisErrorsMessage } from "../../../../errorHandler/utils/errors.utils";
import TextInput from "../../../../../common/components/atom/TextInput/TextInput";
import Button from "../../../../../common/components/atom/Button/Button";
import MessageCard from "../../../../../common/components/molecule/MessageCard/MessageCard";
import styles from "./VerifyPaymentForm.module.scss";
import { useAuth } from "../../../../auth/hooks";

interface VerifyPaymentFormProps {
  walletId?: string;
  onSuccess?: () => void;
}

export default function VerifyPaymentForm({
  walletId: walletIdProp,
  onSuccess,
}: VerifyPaymentFormProps) {
  const { walletId: walletIdParam } = useParams<{ walletId: string }>();
  const walletId = walletIdProp || walletIdParam;
  const { setHeaderAuthToRequest } = useAuth();
  setHeaderAuthToRequest();
  const queryClient = useQueryClient();
  const [kkiapayTxId, setKkiapayTxId] = useState("");
  const [report, setReport] = useState<KkiapayVerificationReport | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);

  const verifyMutation = useMutation({
    mutationFn: () => verifyKkiapayTransaction(kkiapayTxId.trim()),
    onSuccess: (data) => {
      setReport(data);
      setVerifyError(null);
    },
    onError: (error: any) => {
      setReport(null);
      setVerifyError(
        extractAxoisErrorsMessage(error) ||
          "Erreur lors de la vérification de la transaction"
      );
    },
  });

  const registerMutation = useMutation({
    mutationFn: () =>
      registerKkiapayTransaction(kkiapayTxId.trim(), walletId || ""),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallets"] });
      queryClient.invalidateQueries({ queryKey: ["transactions" + (walletId || "")] });
      onSuccess && onSuccess();
    },
    onError: (error: any) => {
      setRegisterError(
        extractAxoisErrorsMessage(error) ||
          "Erreur lors de l'enregistrement de la transaction"
      );
    },
  });

  const handleVerify = () => {
    if (!kkiapayTxId.trim()) return;
    setReport(null);
    setVerifyError(null);
    setRegisterError(null);
    verifyMutation.mutate();
  };

  const handleRegister = () => {
    setRegisterError(null);
    registerMutation.mutate();
  };

  const canRegister =
    report &&
    !report.is_registered &&
    report.kkiapay_status === "SUCCESS";

  return (
    <div className={styles.container}>
      <p className={styles.description}>
        Saisissez l'identifiant de transaction Kkiapay pour vérifier son statut
        et créditer le compte si le paiement n'a pas été enregistré.
      </p>

      <div className={styles.inputRow}>
        <TextInput
          value={kkiapayTxId}
          onChange={(e) => {
            setKkiapayTxId(e.target.value);
            setReport(null);
            setVerifyError(null);
            setRegisterError(null);
          }}
          label="ID de transaction Kkiapay"
          placeholder="Ex: 7163966447772971"
        />
      </div>

      <div className={styles.verifyBtn}>
        <Button
          isLoading={verifyMutation.isLoading}
          onClick={handleVerify}
          disabled={!kkiapayTxId.trim() || verifyMutation.isLoading}
        >
          Vérifier
        </Button>
      </div>

      {verifyError && (
        <div className={styles.messageWrapper}>
          <MessageCard variant="danger" message={verifyError} width100 />
        </div>
      )}

      {report && (
        <div className={styles.report}>
          <h3 className={styles.reportTitle}>Rapport de vérification</h3>

          <div className={styles.reportGrid}>
            <div className={styles.reportItem}>
              <span className={styles.reportLabel}>Statut Kkiapay</span>
              <span
                className={[
                  styles.reportValue,
                  report.kkiapay_status === "SUCCESS"
                    ? styles.success
                    : styles.failed,
                ].join(" ")}
              >
                {report.kkiapay_status}
              </span>
            </div>

            <div className={styles.reportItem}>
              <span className={styles.reportLabel}>Montant</span>
              <span className={styles.reportValue}>
                {report.kkiapay_amount} FCFA
              </span>
            </div>

            <div className={styles.reportItem}>
              <span className={styles.reportLabel}>Enregistré dans le système</span>
              <span
                className={[
                  styles.reportValue,
                  report.is_registered ? styles.success : styles.failed,
                ].join(" ")}
              >
                {report.is_registered ? "Oui" : "Non"}
              </span>
            </div>

            {report.wallet_transactions && report.wallet_transactions.length > 0 && (
              <div className={styles.reportItem}>
                <span className={styles.reportLabel}>
                  Transactions wallet ({report.wallet_transactions.length})
                </span>
                <span className={styles.reportValue}>
                  {report.wallet_transactions
                    .map((t: any) => t.w_transaction_id || t.id)
                    .join(", ")}
                </span>
              </div>
            )}

            {report.charging_sessions && report.charging_sessions.length > 0 && (
              <div className={styles.reportItem}>
                <span className={styles.reportLabel}>
                  Sessions de charge ({report.charging_sessions.length})
                </span>
                <span className={styles.reportValue}>
                  {report.charging_sessions
                    .map((s: any) => s.cs_id || s.id)
                    .join(", ")}
                </span>
              </div>
            )}

            {report.kkiapay_error && (
              <div className={styles.reportItem}>
                <span className={styles.reportLabel}>Erreur Kkiapay</span>
                <span className={[styles.reportValue, styles.failed].join(" ")}>
                  {report.kkiapay_error}
                </span>
              </div>
            )}
          </div>

          {registerError && (
            <div className={styles.messageWrapper}>
              <MessageCard variant="danger" message={registerError} width100 />
            </div>
          )}

          {canRegister && (
            <div className={styles.registerBtn}>
              <Button
                isLoading={registerMutation.isLoading}
                onClick={handleRegister}
                disabled={registerMutation.isLoading}
              >
                Créditer le compte
              </Button>
            </div>
          )}

          {report.is_registered && (
            <div className={styles.messageWrapper}>
              <MessageCard
                variant="info"
                message="Cette transaction est déjà enregistrée dans le système."
                width100
              />
            </div>
          )}

          {!canRegister && !report.is_registered && report.kkiapay_status !== "SUCCESS" && (
            <div className={styles.messageWrapper}>
              <MessageCard
                variant="warring"
                message="La transaction Kkiapay n'est pas en statut SUCCESS. Le crédit ne peut pas être enregistré."
                width100
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
